import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import HyCAPNotRunning from "../../assets/HyCAP_NotRunning.svg";
import HyCAPRunning from "../../assets/HyCAP_Running.svg";
import CardContent from "@mui/material/CardContent";
import { green, red } from "@mui/material/colors";
import { ReactComponent as AuxiliarySystemIcon } from "../../../assets/HyPET_AuxiliarySystem.svg";
import { ReactComponent as MachineIcon } from "../../../assets/HyPET_MachineIcon.svg";
import { ReactComponent as HydraulicIcon } from "../../../HyPET/assets/Hydraulic.svg";
import { ReactComponent as ChilledWaterIcon } from "../../../HyPET/assets/chilledWater.svg";
import { ReactComponent as PneumaticIcon } from "../../../HyPET/assets/Pneumatic.svg";
import { ReactComponent as OperatorEnabledIcon } from "../../../HyPET/assets/OperatorEnabled.svg";
import { ReactComponent as ElectricalIcon } from "../../../HyPET/assets/Electrical.svg";
import { ReactComponent as HVACIcon } from "../../../HyPET/assets/HVAC.svg";
import { ReactComponent as MotorIcon } from "../../../HyPET/assets/Motor.svg";
import { ReactComponent as DehumidifierIcon } from "../../../HyPET/assets/Dehumidifier.svg";
import { ReactComponent as PetteraSystemIcon } from "../../../HyPET/assets/PetteraSystem.svg";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import MachineCanvas from "../MachineCanvas";
import ButtonCubeResponse from "../../../Hooks/ButtonCubeResponse";
import ImageCubeResponse from "../../../Hooks/ImageCubeResponse";
import ConditionalLink from "../../../utils/conditionalLink";
import { useSelector } from "react-redux";

interface ChildAsset {
  subCategory: any;
  kpiDataPoints: any;
  _id: any;
  asset_name: string;
  assetStatus: number;
  subAssets?: ChildAsset[];
}

interface childSubAssets {
  hycapName?: string;
  hycapChildName?: string;
  hycapSubChildName?: any;
}
interface MachineCardProps {
  machineName: string;
  childAssets?: ChildAsset[];
  machneIdentity?: string;
  tag?: string;
  assetId?: string;
  pathParts?: string[];
  hycapId?: any;
  hycapChildId?: any;
  hycapSubChildId?: any;
  asset?: any;
  params?: any;
  parentId?: any;
}

interface DataItem {
  subCategory?: any;
  kpiDataPoints?: any;
  _id: any;
  asset_name: string;
  assetStatus?: number;
  subAssets?: ChildAsset[];
}

const transformArrayToObject = (
  arr: DataItem[],
  tagValue: string,
  parentIdValue: string,
  hycapIdValue: string,
  hycapChildIdValue: string,
  hycapSubChildIdValue: string,
  params: any
): Record<string, any> => {
  return arr.reduce((acc, item) => {
    const { asset_name, _id } = item;
    const lowerCaseDescription = asset_name?.toLowerCase();
    acc[lowerCaseDescription] = {
      assetId: _id,
      tag: tagValue,
      parentId: parentIdValue,
      hycapId: hycapIdValue,
      hycapChildId:
        params.hycap && !params.hycapchild ? _id : hycapChildIdValue,
      hycapSubChildId:
        params.hycap && params.hycapchild && !params.hycapSubChildId
          ? item._id
          : hycapSubChildIdValue,
      asset: item,
    }; // Push _id into the array
    return acc;
  }, {} as Record<string, { assetId: string; tag?: string; parentId?: string; hycapId?: string; hycapChildId?: string; hycapSubChildId?: string; params?: any; asset?: any }>);
};

const InjectionMouldingHyCAPMachineCard: React.FC<MachineCardProps> = ({
  machineName,
  childAssets,
  machneIdentity,
  tag,
  assetId,
  hycapId,
  hycapChildId,
  hycapSubChildId,
  parentId,
  params,
  pathParts = [],
}) => {
  const navigate = useNavigate();
  const [hoveredPart, setHoveredPart] = useState<string>("");
  const { hycap, hycapchild, hycapsubchild } = params;

  const seedsList = useSelector((state: any) => state.seedsList);

  const InjectionAssetSequece = seedsList.seeds.find((obj: any) => {
    return obj.seed_key_name === "InjectionPDMAssetSeq";
  })?.seed_data_value;

  let order: string[] = [];
  InjectionAssetSequece?.forEach((obj: any) => {
    if (obj.key === "level3") {
      order = JSON.parse(obj.value);
    }
  });

  const navigationState = transformArrayToObject(
    childAssets || [],
    tag || "",
    parentId || "",
    hycapId || "",
    hycapChildId || "",
    hycapSubChildId || "",
    params
  );

  const renderIcon = (assetName: string) => {
    switch (assetName) {
      case "Auxiliary Systems":
        return <AuxiliarySystemIcon />;
      case "Systems":
        return <AuxiliarySystemIcon />;
      case "Machines":
        return <MachineIcon />;
      case "Hydraulic":
        return <HydraulicIcon />;
      case "Chilled Water":
        return <ChilledWaterIcon />;
      case "Pneumatic":
        return <PneumaticIcon />;
      case "Operator Enabled":
        return <OperatorEnabledIcon />;
      case "Electrical":
        return <ElectricalIcon />;
      case "HVAC":
        return <HVACIcon />;
      case "Motor":
        return <MotorIcon />;
      case "Dehumidifier":
        return <DehumidifierIcon />;
      case "Pettera System & Bridle Loop":
        return <PetteraSystemIcon />;
      default:
        return <></>;
    }
  };

  // Function to generate the correct path based on hierarchy
  const generateNextPath = (item: ChildAsset) => {
    const newPathParts = [...pathParts, item.asset_name]; // Append new asset to the path
    return item.kpiDataPoints || item.subCategory
      ? `/usecase/injection-pdm/hycaps/${newPathParts.join("/")}/reportview`
      : `/usecase/injection-pdm/hycaps/${newPathParts.join("/")}/machineview`;
  };

  const lookup = (childAssets || []).reduce<Record<string, ChildAsset>>(
    (acc, item) => {
      acc[item.asset_name] = item;
      return acc;
    },
    {}
  );
  const orderedNames = new Set(order);
  const missedItems = (childAssets || []).filter(
    (item) => !orderedNames.has(item.asset_name)
  );
  const arrangedOrder = [
    ...order.map((key) => lookup[key]).filter(Boolean),
    ...missedItems,
  ];

  return (
    <Card>
      {machineName === "Robot" ||
      machineName === "robot" ||
      machineName === "Robots" ||
      machineName === "robots" ||
      machineName === "systems" ||
      machineName === "Systems" ||
      machineName === "auxiliary systems" ||
      machineName === "Auxiliary Systems" ? (
        <></>
      ) : (
        <>
          <CardHeader
            action={
              <ButtonCubeResponse
                tag={`${tag}_ProductionState_PV`}
                limit={1}
                statusParam={{ NR: "1", R: "2" }}
              />
            }
            title={machineName}
          />
          <CardContent sx={{ p: 1 }}>
            <CardMedia
              style={{
                backgroundColor: "#232a58",
                minHeight: "250px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MachineCanvas
                hoveredPart={hoveredPart}
                setHoveredPart={setHoveredPart}
                selectedAsset={arrangedOrder?.[0]?.asset_name}
                machineName={machneIdentity}
                navigationState={navigationState}
              />
            </CardMedia>
          </CardContent>
        </>
      )}

      {/* Render child assets as buttons */}
      <Grid container spacing={1} justifyContent="flex-start" padding={1}>
        {arrangedOrder &&
          arrangedOrder.map((item) => (
            <Grid item key={item.asset_name} xs={12} sm={4} md={3} lg={3}>
              <ConditionalLink
                name={item.asset_name}
                defaultLink={generateNextPath(item)}
                machineLink={`/usecase/injection-pdm/hycaps/${pathParts.join(
                  "/"
                )}/${item.asset_name}/reportview`}
                state={{
                  assetId: item._id,
                  tag: tag,
                  parentId: parentId,
                  hycapId: hycapId,
                  hycapChildId: hycap && !hycapchild ? item._id : hycapChildId,
                  hycapSubChildId:
                    hycap && hycapchild && !hycapsubchild
                      ? item._id
                      : hycapSubChildId,
                  asset: item,
                }}
              >
                <Button
                  sx={{ padding: "20px" }}
                  variant="outlined"
                  size="large"
                  fullWidth
                  style={{
                    backgroundColor: `${
                      hoveredPart === item.asset_name ? "#6A5DFF" : "#232A58"
                    }`,
                    border: "none",
                    justifyContent: "flex-start",
                  }}
                  startIcon={renderIcon(item.asset_name)}
                  onMouseEnter={() => setHoveredPart(item.asset_name)}
                  onMouseLeave={() => setHoveredPart("")}
                >
                  {item.asset_name}
                </Button>
              </ConditionalLink>
            </Grid>
          ))}
      </Grid>
    </Card>
  );
};

export default InjectionMouldingHyCAPMachineCard;
